import { ChevronDown } from '@untitledui/icons';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import { useOrg } from '@/contexts/OrgContext';
import type { SelectSize } from '@/lib/base/forms/select/select';
import {
  CHEVRON_SIZE_CLASSES,
  SELECT_TRIGGER_BASE,
  SELECT_TRIGGER_FOCUS,
  sizes,
} from '@/lib/base/forms/select/select';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import type {
  Pipeline,
  PipelineStageSchema,
} from '@/lib/crm/hooks/usePipelines';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { usePipelineStages } from '@/lib/crm/hooks/usePipelineStages';
import { formatPipelineLabel } from '@/lib/crm/pipeline/utils/formatPipelineLabel';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons';
import { Dot } from '@/lib/foundations/dot-icon';
import { cx } from '@/utils/cx';

import { SelectInputLoading } from './SelectInputStates';

/**
 * Association output from AssociatedPipelineStageInput.
 * Used to create an association between an object and its pipeline/lifecycle stage.
 */
export interface StageAssociationValue {
  association_type: string;
  target_entity_id: string;
}

/** Stage info normalized from both data sources. */
interface StageOption {
  entity_id: string;
  label: string;
  order: number;
  color?: string | null;
}

/** Pipeline group for the dropdown. */
interface PipelineGroup {
  name: string;
  label: string;
  stages: StageOption[];
}

// Sizing comes from the shared field-control scale (`sizes` /
// `CHEVRON_SIZE_CLASSES`) — this picker used to carry its own table whose
// steps didn't line up with the library's, so a stage picker and a Select
// beside it rendered different heights for the same `size` prop.

export interface AssociatedPipelineStageInputProps {
  /** Current selected stage entity_id */
  value: string | null;
  /** Callback with new stage association data */
  onChange: (value: StageAssociationValue | null) => void;
  /** CRM entity type name (e.g., 'ahoy_deal', 'ahoy_contact', 'org_ticket') to filter stages */
  entityType: string;
  /** Association type name (e.g., 'ahoy_deal_to_deal_pipeline_stage') for the onChange output */
  associationTypeName: string;
  /** Optional disabled state. Defaults to false. Provided for accessibility and form state management. */
  disabled?: boolean;
  /** Optional ARIA label for accessibility. Provided for screen reader support when context differs from visible label. */
  ariaLabel?: string;
  /** When true, disables auto-select and adds a clear option in the dropdown. */
  nullable?: boolean;
  /**
   * When true, disables the on-mount auto-select of the default-pipeline first
   * stage. Use in edit-existing-record flows (e.g. the entity overview edit
   * section) where the user opened the form to *modify* the record's current
   * stage — auto-filling a default the user didn't ask for shows up as a
   * spurious "1 unsaved change" the moment Edit is clicked.
   *
   * Differs from `nullable`: `nullable` also adds a "No stage (use default)"
   * trigger label + clear-option menu item, which is desirable in mapping
   * contexts (CSV import) where downstream code applies a default for null,
   * but misleading in plain edit contexts where null just means null.
   * `disableAutoSelect` controls *only* the mount-time effect. The two can be
   * combined, or used independently.
   */
  disableAutoSelect?: boolean;
  /**
   * Trigger size, on the shared field-control scale. Defaults to `'sm'`
   * (40px), the record create/edit form height. Use `'xs'` inside compact
   * surfaces (table cells, the note composer's metadata bar) where the
   * standard trigger reads as oversized next to the surrounding controls.
   */
  size?: SelectSize;
  /**
   * Label shown on the trigger when no stage is selected (nullable mode).
   * Defaults to "No stage (use default)". Also used as the clear-option
   * label in the dropdown.
   */
  nullableLabel?: string;
  /**
   * Popover placement. Defaults to 'bottom start'. Pass 'top start' where the
   * trigger sits near the bottom of its container (e.g. the note composer's
   * bottom metadata bar) so the menu opens upward instead of being clipped.
   */
  placement?: 'bottom start' | 'top start' | 'bottom end' | 'top end';
  /**
   * Open the stage dropdown immediately on mount. Used by inline click-to-edit
   * surfaces (overview rows) where the user's click already expressed "pick a
   * stage" — requiring a second click on the trigger would be a dead step.
   */
  defaultOpen?: boolean;
}

/**
 * AssociatedPipelineStageInput - Single grouped select for pipeline stage associations.
 *
 * For deal entities, uses usePipelines (association-based resolution) to avoid
 * duplicate stages. For contact entities, falls back to useStages.
 *
 * Shows a dropdown with stages grouped by pipeline name:
 * - Pipeline A (section header)
 *   - Stage 1
 *   - Stage 2
 * - Pipeline B (section header)
 *   - Stage 3
 *   - Stage 4
 *
 * Trigger shows "{Pipeline}: {Stage}" when a stage is selected.
 * When only one pipeline exists, trigger shows just the stage name.
 *
 * @example
 * <AssociatedPipelineStageInput
 *   value="entity-id-of-selected-stage"
 *   onChange={(assoc) => console.log(assoc?.target_entity_id)}
 *   entityType="ahoy_deal"
 * />
 */
export function AssociatedPipelineStageInput({
  value,
  onChange,
  entityType,
  associationTypeName,
  disabled = false,
  ariaLabel,
  nullable = false,
  disableAutoSelect = false,
  size = 'sm',
  nullableLabel = 'No stage (use default)',
  placement = 'bottom start',
  defaultOpen = false,
}: AssociatedPipelineStageInputProps) {
  const { currentOrg } = useOrg();
  const id = useId();
  // Mirrors the dropdown's open state for the trigger's ring (see below).
  const [isOpen, setIsOpen] = useState(defaultOpen ?? false);

  // Contact lifecycle stages use useStages (property-based, no pipeline entity).
  // All other entity types (deals, custom entities with pipelines) use usePipelines
  // which resolves stages via pipeline associations and supports entity_type filtering.
  const isContactLifecycle = entityType === 'ahoy_contact';

  // Get icon for the stage entity type
  const stageTypeName = isContactLifecycle
    ? 'ahoy_contact_lifecycle_stage'
    : 'ahoy_deal_pipeline_stage';
  const StageIcon = useMemo(
    () => getEntityTypeIconComponent(stageTypeName),
    [stageTypeName]
  );

  // usePipelines: for deals and custom entities with pipelines (filtered by entityType)
  const { pipelines, isLoading: isPipelinesLoading } = usePipelines(
    !isContactLifecycle ? currentOrg?.id : undefined,
    !isContactLifecycle ? entityType : undefined
  );

  // useStages: only for contact lifecycle stages (property-based resolution)
  const { getStagesByType, isLoading: isStagesLoading } = usePipelineStages(
    isContactLifecycle ? currentOrg?.id : undefined,
    isContactLifecycle ? 'ahoy_contact' : undefined
  );

  const isLoading = isContactLifecycle ? isStagesLoading : isPipelinesLoading;

  // Build pipeline groups from the appropriate data source
  const pipelineGroups = useMemo<PipelineGroup[]>(() => {
    if (!isContactLifecycle) {
      // Deal + custom entities with pipelines: use usePipelines (association-based resolution)
      return [...pipelines]
        .sort((a, b) => {
          // Default pipeline first
          if (a.is_default && !b.is_default) {
            return -1;
          }
          if (!a.is_default && b.is_default) {
            return 1;
          }
          return 0;
        })
        .map((pipeline: Pipeline) => ({
          name: pipeline.pipeline_name,
          label: pipeline.label || formatPipelineLabel(pipeline.pipeline_name),
          stages: pipeline.stages
            .filter((s: PipelineStageSchema) => s.entity_id != null)
            .sort(
              (a: PipelineStageSchema, b: PipelineStageSchema) =>
                a.properties.order - b.properties.order
            )
            .map((s: PipelineStageSchema) => ({
              entity_id: s.entity_id ?? '',
              label: s.label,
              order: s.properties.order,
              color: s.properties.color,
            })),
        }))
        .filter(g => g.stages.length > 0);
    }

    // Contact lifecycle stages from useStages (property-based)
    const contactStages = getStagesByType('ahoy_contact_lifecycle_stage');
    if (contactStages.length === 0) {
      return [];
    }

    // Group by pipeline_name property
    const grouped = new Map<string, StageOption[]>();
    for (const stage of contactStages) {
      const pipelineName = stage.pipeline_name || 'default';
      if (!grouped.has(pipelineName)) {
        grouped.set(pipelineName, []);
      }
      grouped.get(pipelineName)?.push({
        entity_id: stage.entity_id,
        label: stage.label,
        order: stage.order,
        color: stage.color,
      });
    }

    // Sort stages within each group
    for (const stages of grouped.values()) {
      stages.sort((a, b) => a.order - b.order);
    }

    return Array.from(grouped.entries()).map(([name, stages]) => ({
      name,
      label: formatPipelineLabel(name),
      stages,
    }));
  }, [isContactLifecycle, pipelines, getStagesByType]);

  // Flat list of all stages for lookups
  const allStages = useMemo(
    () => pipelineGroups.flatMap(g => g.stages),
    [pipelineGroups]
  );

  const hasMultiplePipelines = pipelineGroups.length > 1;

  // Auto-select the first stage of the default pipeline when:
  // 1. No value is set (null/undefined) — e.g., "Add" button without explicit initialValues
  // 2. Value references a stage that no longer exists (e.g., pipeline was deleted)
  //
  // This is safe because CrmCreateForm's initialization effect runs synchronously on mount
  // (before async stage data loads). If the parent provided initialValues for the stage,
  // the form will have set the value before this effect fires. If value is still null when
  // stages finish loading, it means no initial value was provided and we should default.
  useEffect(() => {
    if (nullable || disableAutoSelect) {
      // nullable mode: caller controls value; no auto-select.
      // disableAutoSelect: edit context; preserve the record's actual stage
      // (or lack thereof) instead of silently defaulting.
      return;
    }
    if (isLoading) {
      return;
    }
    if (allStages.length === 0) {
      return;
    }

    // No value set — auto-select first stage of default pipeline.
    // pipelineGroups[0] is the default pipeline because the memo above sorts
    // by is_default descending (for deal/custom entities) or uses natural order
    // (for contact lifecycle stages which have a single pipeline group).
    //
    // Deferred via queueMicrotask to avoid racing with parent form initialization.
    // React runs child effects before parent effects in the same commit, so a direct
    // setFormData(initialData) in the parent would overwrite our updater-based onChange.
    // By deferring, we ensure the parent's init has committed before we set the default.
    if (!value) {
      const firstStage = pipelineGroups[0]?.stages[0];
      if (firstStage) {
        let cancelled = false;
        queueMicrotask(() => {
          if (!cancelled) {
            onChange({
              association_type: associationTypeName,
              target_entity_id: firstStage.entity_id,
            });
          }
        });
        return () => {
          cancelled = true;
        };
      }
      return;
    }

    // Value is set but doesn't match any known stage — auto-correct to first stage
    const valueExists = allStages.some(s => s.entity_id === value);
    if (!valueExists) {
      const firstStage = pipelineGroups[0]?.stages[0];
      if (firstStage) {
        onChange({
          association_type: associationTypeName,
          target_entity_id: firstStage.entity_id,
        });
      }
    }
  }, [
    nullable,
    disableAutoSelect,
    isLoading,
    value,
    allStages,
    pipelineGroups,
    onChange,
    associationTypeName,
    entityType,
  ]);

  const handleStageSelect = useCallback(
    (stageEntityId: string) => {
      if (stageEntityId === '__no_stage__') {
        onChange(null);
        return;
      }
      onChange({
        association_type: associationTypeName,
        target_entity_id: stageEntityId,
      });
    },
    [onChange, associationTypeName]
  );

  // Build display label for the trigger
  const triggerLabel = useMemo(() => {
    if (!value) {
      return nullable ? nullableLabel : 'Select stage';
    }
    // Find stage in groups
    for (const group of pipelineGroups) {
      const stage = group.stages.find(s => s.entity_id === value);
      if (stage) {
        if (hasMultiplePipelines) {
          return `${group.label}: ${stage.label}`;
        }
        return stage.label;
      }
    }
    return 'Select stage';
  }, [nullable, nullableLabel, value, pipelineGroups, hasMultiplePipelines]);

  // Keep the dropdown's selection in sync with the trigger. In nullable mode,
  // a null value corresponds to the "__no_stage__" menu item, so highlight it.
  const selectedKeys = useMemo(() => {
    if (value) {
      return new Set([value]);
    }
    if (nullable) {
      return new Set(['__no_stage__']);
    }
    return new Set<string>();
  }, [value, nullable]);

  if (isLoading) {
    return <SelectInputLoading />;
  }

  const sizeClasses = sizes[size];

  return (
    <div className='w-full in-data-input-wrapper:flex-1'>
      <Dropdown.Root defaultOpen={defaultOpen} onOpenChange={setIsOpen}>
        {/* Focus ring while OPEN as well as on keyboard focus — the same rule
            as the library Select. Focus sits in the portaled menu while the
            dropdown is open, so a focus-visible-only ring would leave the
            trigger looking idle exactly when the user is working in it. */}
        <AriaButton
          id={id}
          isDisabled={disabled}
          aria-label={ariaLabel}
          className={({ isFocusVisible }) =>
            cx(
              SELECT_TRIGGER_BASE,
              'gap-2 text-primary',
              sizeClasses.root,
              (isOpen || isFocusVisible) && SELECT_TRIGGER_FOCUS,
              'disabled:cursor-not-allowed disabled:bg-disabled_subtle disabled:text-disabled'
            )
          }
        >
          <StageIcon
            aria-hidden='true'
            className={cx('shrink-0 text-fg-quaternary', sizeClasses.icon)}
          />
          <span className='truncate text-left flex-1'>{triggerLabel}</span>
          <ChevronDown
            aria-hidden='true'
            className={cx(
              'ml-auto shrink-0 text-fg-quaternary',
              CHEVRON_SIZE_CLASSES[size]
            )}
          />
        </AriaButton>
        <Dropdown.Popover
          className='min-w-[var(--trigger-width)] w-auto max-w-64'
          placement={placement}
        >
          <Dropdown.Menu
            onAction={key => handleStageSelect(key as string)}
            selectedKeys={selectedKeys}
          >
            {nullable && (
              <Dropdown.Item
                key='__no_stage__'
                id='__no_stage__'
                textValue={nullableLabel}
              >
                <span className='text-fg-tertiary italic'>{nullableLabel}</span>
              </Dropdown.Item>
            )}
            {hasMultiplePipelines
              ? pipelineGroups.map(group => (
                  <Dropdown.Section key={group.name}>
                    <Dropdown.SectionHeader className='px-4 py-1.5 text-caption text-fg-quaternary'>
                      {group.label}
                    </Dropdown.SectionHeader>
                    {group.stages.map(stage => (
                      <Dropdown.Item
                        key={stage.entity_id}
                        id={stage.entity_id}
                        textValue={stage.label}
                      >
                        {stage.color && (
                          <Dot
                            size='sm'
                            style={{ color: stage.color }}
                            aria-hidden='true'
                          />
                        )}
                        {stage.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Section>
                ))
              : allStages.map(stage => (
                  <Dropdown.Item
                    key={stage.entity_id}
                    id={stage.entity_id}
                    textValue={stage.label}
                  >
                    {stage.color && (
                      <Dot
                        size='sm'
                        style={{ color: stage.color }}
                        aria-hidden='true'
                      />
                    )}
                    {stage.label}
                  </Dropdown.Item>
                ))}
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown.Root>
    </div>
  );
}
